import React, { useState, useCallback, useEffect } from "react";
// Remove unused old components
// import { LineChartComponent } from './components/LineChartComponent.tsx';
// import { BarChartComponent } from './components/BarChartComponent.tsx';
// import { PieChartComponent } from './components/PieChartComponent.tsx';
// import { AreaChartComponent } from './components/AreaChartComponent.tsx';
// import { DataAnalysis } from './components/DataAnalysis.tsx';
// import { EdaAnalysis } from './components/EdaAnalysis.tsx';
// import { DrehmomentChart } from './components/DrehmomentChart.tsx';
// import { GradientDistributionChart } from './components/GradientDistributionChart.tsx';
// import { ReibwertChart } from './components/ReibwertChart.tsx';
// import { RadarChartComponent } from './components/RadarChartComponent.tsx';
// import { StepFrequencyChart } from './components/StepFrequencyChart.tsx';
import "./styles/App.css";
import logo from "./logo.jpg";
import { EdaData, AnalysisResult, ModelDataResponse } from "./types";
import { PathInputForm } from "./components/input/PathInputForm.tsx";
// Import the new EDA components
import { EdaInsights } from "./components/eda/EdaInsights.tsx";
import { EdaInsightsPieChart } from "./components/eda/EdaInsightsPieChart.tsx";
import { MissingValuesChart } from "./components/eda/MissingValuesChart.tsx";
import { FeatureDistributionChart } from "./components/eda/FeatureDistributionChart.tsx";
// Importiere die neue Komponente für kategoriale Features
import { CategoricalFeatureDistributionChart } from "./components/eda/CategoricalFeatureDistributionChart.tsx";
// Import the new Correlation Bar Chart component
import { CorrelationBarChart } from "./components/eda/CorrelationBarChart.tsx";
// Import new model components
import { ModelInfoCard } from "./components/model/ModelInfoCard.tsx";
import { FeatureImportanceChart } from "./components/model/FeatureImportanceChart.tsx";
import { ModelMetricsTable } from "./components/model/ModelMetricsTable.tsx";
// Import the new ModelPerformanceChart
import { ModelPerformanceChart } from "./components/model/ModelPerformanceChart.tsx";
// Import the new PredictionForm component (uncomment)
import { PredictionForm } from "./components/predict/PredictionForm.tsx";
// Import the new PredictionResultDashboard component
import { PredictionResultDashboard } from "./components/predict/PredictionResultDashboard.tsx";
import { LabelDistributionChart } from "./components/eda/LabelDistributionChart.tsx";
// Importiere die neue Seite für die Sensitivitätsanalyse
import { SensitivityAnalysisPage } from "./components/eda/SensitivityAnalysisPage.tsx";
// Import the new PSO Optimization Page
import { PSOOptimizationPage } from "./components/pso/PSOOptimizationPage.tsx";

// Define type for EDA view state
type EdaInfoView = "text" | "chart" | "both";

// Removed sampleData

// Function to get initial theme from localStorage or system preference
const getInitialTheme = (): "light" | "dark" => {
  const storedTheme = localStorage.getItem("theme");
  if (storedTheme === "light" || storedTheme === "dark") {
    return storedTheme;
  }
  // Fallback to system preference if no theme stored
  if (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light"; // Default to light
};

function App() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Default to eda_info if possible, otherwise null/placeholder
  const [activeSection, setActiveSection] = useState<string | null>(
    "pso_optimization",
  );

  // State for fetched data
  const [edaData, setEdaData] = useState<EdaData | null>(null);
  const [modelData, setModelData] = useState<ModelDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentEdaPath, setCurrentEdaPath] = useState<string>("");
  const [currentModelPath, setCurrentModelPath] = useState<string>("");
  // Add state for distribution chart toggle
  const [showAllDistributions, setShowAllDistributions] =
    useState<boolean>(false);
  // Add state for EDA Insights view toggle
  const [edaInfoView, setEdaInfoView] = useState<EdaInfoView>("both"); // Default to showing both

  // --- NEU: State für die Sichtbarkeit des Eingabeformulars ---
  const [isPathInputVisible, setIsPathInputVisible] = useState<boolean>(true);

  // --- NEUE STATES für kategoriale Diagramme ---
  const [showAllCategoricalDistributions, setShowAllCategoricalDistributions] =
    useState<boolean>(false);
  const [selectedCategoricalFeature, setSelectedCategoricalFeature] =
    useState<string>("");
  // --- Ende NEUE STATES ---

  // --- Prediction States (Moved from PredictionForm) ---
  const [isPredicting, setIsPredicting] = useState<boolean>(false);
  const [predictionError, setPredictionError] = useState<string | null>(null);
  const [predictionResult, setPredictionResult] = useState<Record<
    string,
    number
  > | null>(null);
  // --- End Prediction States ---

  // --- Dark Mode State ---
  const [theme, setTheme] = useState<"light" | "dark">(getInitialTheme);

  // --- NEW STATES for Label Distributions ---
  const [showAllLabelDistributions, setShowAllLabelDistributions] =
    useState<boolean>(false);
  const [selectedLabel, setSelectedLabel] = useState<string>("");
  // --- END NEW STATES for Label Distributions ---

  // Effekt zum Lesen von URL-Query-Parametern beim ersten Laden
  useEffect(() => {
    // Diese Funktion wird nur einmal beim Mounten der Komponente ausgeführt.
    const params = new URLSearchParams(window.location.search);
    const edaPathFromUrl = params.get("eda_path");
    const modelPathFromUrl = params.get("model_path");

    // Wenn einer der Pfade in der URL vorhanden ist, lade die Daten.
    // Die fetchData Funktion kümmert sich um das Setzen der States (currentEdaPath etc.).
    if (edaPathFromUrl || modelPathFromUrl) {
      fetchData(edaPathFromUrl || "", modelPathFromUrl || "");
    }
    // Die leere Dependency-Array stellt sicher, dass dieser Effekt nur einmal ausgeführt wird.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ACHTUNG: fetchData ist hier bewusst nicht als Abhängigkeit drin, um mehrfaches Fetchen zu vermeiden.

  // Effect to save theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("theme", theme);
    // Optional: Add/remove class from body for global styling access if needed
    // document.body.classList.remove('light-theme', 'dark-theme');
    // document.body.classList.add(`${theme}-theme`);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };
  // --- End Dark Mode State ---

  // Effekt zum Setzen des initial ausgewählten kategorialen Features
  useEffect(() => {
    if (
      edaData &&
      edaData.eda_results.features_stats.categorical_feature_names.length > 0
    ) {
      const catFeatures =
        edaData.eda_results.features_stats.categorical_feature_names;
      setSelectedCategoricalFeature((prevSelected) => {
        if (!prevSelected || !catFeatures.includes(prevSelected)) {
          return catFeatures[0];
        }
        return prevSelected;
      });
    } else {
      setSelectedCategoricalFeature("");
    }
  }, [edaData]);

  // Effekt zum Setzen des initial ausgewählten Labels
  useEffect(() => {
    const labelNames = edaData
      ? ((edaData.eda_results.labels_stats as any).target_names as string[])
      : [];
    if (labelNames && labelNames.length > 0) {
      setSelectedLabel((prevSelected) => {
        if (!prevSelected || !labelNames.includes(prevSelected)) {
          return labelNames[0];
        }
        return prevSelected;
      });
    } else {
      setSelectedLabel("");
    }
  }, [edaData]);

  const fetchData = useCallback(
    async (edaPath: string, modelPath: string) => {
      setIsLoading(true);
      setError(null);
      setEdaData(null); // Clear previous data
      setModelData(null); // Clear previous data
      setCurrentEdaPath(edaPath);
      setCurrentModelPath(modelPath);
      const API_BASE_URL =
        "https://predict-func-blob.wittyground-0065b41e.germanywestcentral.azurecontainerapps.io";
      // Reset active section if the current one becomes unavailable
      let nextActiveSection = activeSection;
      if (activeSection?.startsWith("eda_") && !edaPath) {
        nextActiveSection = modelPath ? "model_info" : null;
      }
      if (activeSection?.startsWith("model_") && !modelPath) {
        nextActiveSection = edaPath ? "pso_optimization" : null;
      }
      if (!edaPath && !modelPath) {
        nextActiveSection = null; // No data source, no section
      } else if (!activeSection) {
        // If no section was active, default to first available
        nextActiveSection = edaPath ? "pso_optimization" : "model_info";
      }
      setActiveSection(nextActiveSection);

      // Reset prediction state when new model/data is loaded
      setPredictionResult(null);
      setPredictionError(null);
      setIsPredicting(false);

      // Reset states for categorical charts when new data is fetched
      setShowAllCategoricalDistributions(false);

      try {
        let fetchedEda: EdaData | null = null;
        let fetchedModel: ModelDataResponse | null = null;

        // Fetch EDA data if path is provided
        if (edaPath) {
          console.log(
            `Fetching EDA data from: /get_analysis_results?eda_path=${edaPath}`,
          );
          // const response = await fetch(
          //   `/get_analysis_results?eda_path=${encodeURIComponent(edaPath)}`,
          // );
          const response = await fetch(
            `${API_BASE_URL}/get_analysis_results?eda_path=${encodeURIComponent(edaPath)}`,
          );
          if (!response.ok) {
            let errorMsg = `Fehler beim Laden der EDA Daten (${response.status})`;
            try {
              const errorData = await response.json();
              errorMsg += `: ${errorData.error || response.statusText}`;
            } catch (jsonError) {
              errorMsg += `: ${response.statusText}`;
            }
            throw new Error(errorMsg);
          }
          const data: AnalysisResult = await response.json();
          // Type guard to check if it's EdaData
          if ("eda_results" in data) {
            fetchedEda = data as EdaData;
            console.log("EDA Data loaded:", fetchedEda);
          } else {
            console.warn(
              "Received non-EDA data when expecting EDA data for path:",
              edaPath,
              data,
            );
            // Optionally set an error or just leave fetchedEda as null
          }
        }

        // Fetch Model data if path is provided
        if (modelPath) {
          console.log(
            `Fetching Model data from: /get_analysis_results?model_path=${modelPath}`,
          );
          // const response = await fetch(
          //   `/get_analysis_results?model_path=${encodeURIComponent(modelPath)}`,
          // );
          const response = await fetch(
            `${API_BASE_URL}/get_analysis_results?model_path=${encodeURIComponent(modelPath)}`,
          );
          if (!response.ok) {
            let errorMsg = `Fehler beim Laden der Model Daten (${response.status})`;
            try {
              const errorData = await response.json();
              errorMsg += `: ${errorData.error || response.statusText}`;
            } catch (jsonError) {
              errorMsg += `: ${response.statusText}`;
            }
            throw new Error(errorMsg);
          }
          const data: AnalysisResult = await response.json();
          // Type guard to check if it's ModelDataResponse
          if (
            "model_metrics" in data &&
            "model_name" in data &&
            !("eda_results" in data)
          ) {
            fetchedModel = data as ModelDataResponse;
            console.log("Model Data (Info JSON) loaded:", fetchedModel);
          } else {
            console.warn(
              "Received non-Model data or unexpected model format for path:",
              modelPath,
              data,
            );
            // Optional: Fehler setzen, wenn das Format nicht passt
            // setError("Modelldaten in unerwartetem Format empfangen.");
          }
        }

        setEdaData(fetchedEda);
        setModelData(fetchedModel);

        // Bei erfolgreichem Laden das Formular einklappen
        if (fetchedEda || fetchedModel) {
          setIsPathInputVisible(false);
        } else {
          setIsPathInputVisible(true);
        }
      } catch (err: any) {
        console.error("Fetch error:", err);
        setError(err.message || "Ein unbekannter Fehler ist aufgetreten.");
        // Bei einem Fehler das Formular offen lassen, um Korrekturen zu ermöglichen
        setIsPathInputVisible(true);
      } finally {
        setIsLoading(false);
      }
      // Removed activeSection from dependency array as we handle it inside
    },
    [activeSection],
  ); // We still need activeSection here to reset it correctly

  // --- handlePredict (New function for API call) ---
  const handlePredict = useCallback(
    async (featuresPayload: Record<string, string | number>) => {
      if (!modelData || !modelData.model_path) {
        setPredictionError(
          "Kein Modellpfad zum Ausführen der Vorhersage verfügbar.",
        );
        return;
      }
      const API_BASE_URL =
        "https://predict-func-blob.wittyground-0065b41e.germanywestcentral.azurecontainerapps.io";
      setIsPredicting(true);
      setPredictionError(null);
      setPredictionResult(null);

      console.log("App.tsx: Submitting to /predict:", {
        model_path: modelData.model_path,
        features: featuresPayload,
      });

      try {
        // const response = await fetch("/api/predict", {
        //   method: "POST",
        //   headers: {
        //     "Content-Type": "application/json",
        //   },
        //   body: JSON.stringify({
        //     model_path: modelData.model_path,
        //     features: featuresPayload,
        //   }),
        // });

        const response = await fetch(`${API_BASE_URL}/predict`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model_path: modelData.model_path,
            features: featuresPayload,
          }),
        });

        const resultData = await response.json();

        if (!response.ok) {
          throw new Error(
            resultData.error ||
              `HTTP Fehler ${response.status}: ${response.statusText}`,
          );
        }

        if (resultData && resultData.predictions) {
          setPredictionResult(resultData.predictions);
          // Optionally switch to the dashboard after successful prediction?
          // setActiveSection('predict_dashboard');
        } else {
          console.error(
            "App.tsx: Unexpected prediction response structure:",
            resultData,
          );
          throw new Error("Unerwartete Antwortstruktur vom Server erhalten.");
        }
      } catch (err: any) {
        console.error("App.tsx: Prediction API call failed:", err);
        setPredictionError(
          err.message || "Ein unbekannter Fehler ist aufgetreten.",
        );
        setPredictionResult(null);
      } finally {
        setIsPredicting(false);
      }
      // Depend on currentModelPath to ensure it's available when called
    },
    [modelData],
  );

  // --- Keep Sidebar structure ---
  const menuSections = {
    // Adjusted section names for clarity
    eda: [
      { id: "eda_info", name: "EDA Insights", icon: "👁️" },
      { id: "eda_dashboard", name: "EDA Dashboard", icon: "📋" },
    ],
    model: [
      { id: "model_info", name: "Model Info", icon: "🔧" },
      { id: "model_dashboard", name: "Model Metriken", icon: "📊" }, // Changed Icon
      { id: "predict", name: "Predict", icon: "🔮" },
      // Add new Dashboard section
      { id: "predict_dashboard", name: "Predict Dashboard", icon: "📈" },
    ],
    model_insights: [
      { id: "sensitivity_analysis", name: "Sensitivitätsanalyse", icon: "🕸️" },
      { id: "pso_optimization", name: "PSO-Optimierung", icon: "🧬" },
    ],
    others: [
      // Keep other sections as they were
      { id: "notifications", name: "Notification", icon: "🔔" },
      { id: "users", name: "People", icon: "👥" },
    ],
    documents: [
      { id: "file_manager", name: "File Manager", icon: "📄", favorite: true },
      {
        id: "documentation",
        name: "Documentation",
        icon: "📑",
        favorite: true,
      },
    ],
  };

  // --- Reworked sectionComponents ---
  // This will render content based on the fetched data
  const renderSectionContent = () => {
    if (isLoading)
      return <div className="loading-indicator">Daten werden geladen...</div>;
    if (error) return <div className="error-message">Fehler: {error}</div>;

    // Only render EDA/Model sections if corresponding path was provided
    const canShowEda = !!currentEdaPath && !!edaData;
    const canShowModel = !!currentModelPath && !!modelData;

    // Helper function to cycle through view modes
    const getNextEdaView = (currentView: EdaInfoView): EdaInfoView => {
      if (currentView === "text") return "chart";
      if (currentView === "chart") return "both";
      return "text"; // Cycle from 'both' back to 'text'
    };

    switch (activeSection) {
      case "eda_info":
        // Render based on edaInfoView state
        return canShowEda ? (
          <div className="eda-insights-section">
            {/* View Toggle Button */}
            <div className="view-toggle card">
              <button
                onClick={() => setEdaInfoView(getNextEdaView(edaInfoView))}
                className={`toggle-button`}
                title={`Wechseln zu: ${getNextEdaView(edaInfoView)}`}
              >
                Ansicht:{" "}
                {edaInfoView.charAt(0).toUpperCase() + edaInfoView.slice(1)}{" "}
                <span className="toggle-icon">🔄</span>
              </button>
            </div>

            {/* Conditional Rendering based on state */}
            {(edaInfoView === "text" || edaInfoView === "both") && (
              <EdaInsights edaData={edaData} currentEdaPath={currentEdaPath} />
            )}
            {(edaInfoView === "chart" || edaInfoView === "both") && (
              <EdaInsightsPieChart edaData={edaData} />
            )}
          </div>
        ) : (
          <p className="placeholder-message">
            Bitte geben Sie einen gültigen EDA Pfad an und laden Sie die Daten,
            um Insights anzuzeigen.
          </p>
        );

      case "eda_dashboard":
        // Use the EDA chart components
        return canShowEda ? (
          <div className="eda-dashboard-layout">
            {" "}
            {/* Changed class for potential specific styling */}
            {/* Controls for the dashboard */}
            <div className="dashboard-controls card">
              {/* Numerische Features Controls */}
              <div className="control-item">
                <label htmlFor="toggle-distributions">
                  Alle numerischen Verteilungen anzeigen:
                </label>
                <input
                  type="checkbox"
                  id="toggle-distributions"
                  checked={showAllDistributions}
                  onChange={(e) => setShowAllDistributions(e.target.checked)}
                />
              </div>
              {/* Kategoriale Features Controls */}
              {edaData.eda_results.features_stats.categorical_feature_names
                .length > 0 && (
                <div className="control-item">
                  <label htmlFor="toggle-categorical-distributions">
                    Alle kategorialen Verteilungen anzeigen:
                  </label>
                  <input
                    type="checkbox"
                    id="toggle-categorical-distributions"
                    checked={showAllCategoricalDistributions}
                    onChange={(e) =>
                      setShowAllCategoricalDistributions(e.target.checked)
                    }
                  />
                </div>
              )}
              {/* Label-Distributions Controls */}
              {Array.isArray(
                (edaData.eda_results.labels_stats as any).target_names,
              ) &&
                (edaData.eda_results.labels_stats as any).target_names.length >
                  0 && (
                  <div className="control-item">
                    <label htmlFor="toggle-label-distributions">
                      Alle Label-Verteilungen anzeigen:
                    </label>
                    <input
                      type="checkbox"
                      id="toggle-label-distributions"
                      checked={showAllLabelDistributions}
                      onChange={(e) =>
                        setShowAllLabelDistributions(e.target.checked)
                      }
                    />
                  </div>
                )}
            </div>
            {/* --- Numerische Verteilungen --- */}
            <h3 className="dashboard-section-title">
              Numerische Feature-Verteilungen
            </h3>
            {showAllDistributions ? (
              <div className="content-grid">
                {" "}
                {/* Use grid for multiple charts */}
                {edaData.eda_results.features_stats.numeric_feature_names.map(
                  (featureName) => (
                    <div key={featureName} className="chart-container-wrapper">
                      <FeatureDistributionChart
                        edaResults={edaData.eda_results}
                        initialFeature={featureName}
                      />
                    </div>
                  ),
                )}
                {edaData.eda_results.features_stats.numeric_feature_names
                  .length === 0 && <p>Keine numerischen Features vorhanden.</p>}
              </div>
            ) : (
              <div className="chart-container-wrapper">
                <FeatureDistributionChart edaResults={edaData.eda_results} />
              </div>
            )}
            {/* --- Kategoriale Verteilungen (Angepasste Logik) --- */}
            {edaData.eda_results.features_stats.categorical_feature_names
              .length > 0 && (
              <>
                <h3 className="dashboard-section-title">
                  Kategoriale Feature-Verteilungen
                </h3>
                {showAllCategoricalDistributions ? (
                  // Zeige alle Diagramme (Grid-Modus)
                  <div className="content-grid">
                    {edaData.eda_results.features_stats.categorical_feature_names.map(
                      (featureName) => {
                        const featureStats =
                          edaData.eda_results.features_stats.stats_per_feature[
                            featureName
                          ];
                        if (!featureStats) return null;
                        return (
                          <div
                            key={featureName}
                            className="chart-container-wrapper"
                          >
                            <CategoricalFeatureDistributionChart
                              featureName={featureName} // Wichtig für Key und internen Fallback
                              featureStats={featureStats}
                              totalSamples={
                                edaData.eda_results.features_stats.n_samples
                              }
                              showSelector={false} // Kein Selector im Grid-Modus
                            />
                          </div>
                        );
                      },
                    )}
                  </div>
                ) : (
                  // Zeige nur das ausgewählte Diagramm (Einzelmodus)
                  <div className="chart-container-wrapper">
                    {selectedCategoricalFeature &&
                    edaData.eda_results.features_stats.stats_per_feature[
                      selectedCategoricalFeature
                    ] ? (
                      <CategoricalFeatureDistributionChart
                        // featureName ist hier weniger kritisch, da selectedFeature Priorität hat
                        featureName={selectedCategoricalFeature}
                        featureStats={
                          edaData.eda_results.features_stats.stats_per_feature[
                            selectedCategoricalFeature
                          ]
                        }
                        totalSamples={
                          edaData.eda_results.features_stats.n_samples
                        }
                        // Props für das interne Dropdown:
                        allCategoricalFeatures={
                          edaData.eda_results.features_stats
                            .categorical_feature_names
                        }
                        selectedFeature={selectedCategoricalFeature}
                        onFeatureSelect={setSelectedCategoricalFeature} // State-Setter übergeben
                        showSelector={true} // Selector anzeigen
                      />
                    ) : (
                      <p>
                        Bitte wählen Sie ein kategoriales Feature aus oder laden
                        Sie Daten.
                      </p> // Angepasster Fallback
                    )}
                  </div>
                )}
              </>
            )}
            {/* Ende Kategoriale Verteilungen */}
            {/* --- Label-Verteilungen (neu) --- */}
            {Array.isArray(
              (edaData.eda_results.labels_stats as any).target_names,
            ) &&
              (edaData.eda_results.labels_stats as any).target_names.length >
                0 && (
                <>
                  <h3 className="dashboard-section-title">
                    Label-Verteilungen
                  </h3>
                  {showAllLabelDistributions ? (
                    <div className="content-grid">
                      {(
                        edaData.eda_results.labels_stats as any
                      ).target_names.map((labelName: string) => {
                        const labelStats = (
                          edaData.eda_results.labels_stats as any
                        ).stats[labelName];
                        const dist =
                          edaData.eda_results.labels_distribution_analysis?.[
                            labelName
                          ];
                        const bins = dist?.histogram_bins;
                        const edges = dist?.histogram_edges;
                        return (
                          <div
                            key={labelName}
                            className="chart-container-wrapper"
                          >
                            <LabelDistributionChart
                              labelName={labelName}
                              labelStats={labelStats}
                              histogramBins={bins}
                              histogramEdges={edges}
                              quartiles={dist?.quartiles}
                              mean={labelStats?.mean}
                              isCategorical={false}
                              barColor="#4CAF50"
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="chart-container-wrapper">
                      {/* Dropdown für Label-Auswahl */}
                      <div
                        className="control-item"
                        style={{ marginBottom: "1rem" }}
                      >
                        <label htmlFor="select-label">Label auswählen:</label>
                        <select
                          id="select-label"
                          value={selectedLabel}
                          onChange={(e) => setSelectedLabel(e.target.value)}
                          style={{ marginLeft: 8 }}
                        >
                          {(
                            edaData.eda_results.labels_stats as any
                          ).target_names.map((labelName: string) => (
                            <option key={labelName} value={labelName}>
                              {labelName}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedLabel &&
                      (edaData.eda_results.labels_stats as any).stats[
                        selectedLabel
                      ] ? (
                        <LabelDistributionChart
                          labelName={selectedLabel}
                          labelStats={
                            (edaData.eda_results.labels_stats as any).stats[
                              selectedLabel
                            ]
                          }
                          histogramBins={
                            edaData.eda_results.labels_distribution_analysis?.[
                              selectedLabel
                            ]?.histogram_bins
                          }
                          histogramEdges={
                            edaData.eda_results.labels_distribution_analysis?.[
                              selectedLabel
                            ]?.histogram_edges
                          }
                          quartiles={
                            edaData.eda_results.labels_distribution_analysis?.[
                              selectedLabel
                            ]?.quartiles
                          }
                          mean={
                            (edaData.eda_results.labels_stats as any).stats[
                              selectedLabel
                            ]?.mean
                          }
                          isCategorical={false}
                          barColor="#4CAF50"
                        />
                      ) : (
                        <p>
                          Bitte wählen Sie ein Label aus oder laden Sie Daten.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            {/* --- Ende Label-Verteilungen --- */}
            {/* --- Missing Values Chart --- */}
            <h3 className="dashboard-section-title">Fehlende Werte</h3>
            <div className="chart-container-wrapper">
              <MissingValuesChart edaResults={edaData.eda_results} />
            </div>
            {/* --- Korrelationen --- */}
            <h3 className="dashboard-section-title">
              Korrelationen (Numerisch)
            </h3>
            <div className="correlation-info-wrapper">
              {edaData.eda_results.correlation?.matrix ? (
                <CorrelationBarChart
                  correlationData={edaData.eda_results.correlation}
                />
              ) : (
                <p className="placeholder-message">
                  {edaData.eda_results.correlation?.message ||
                    "Keine Korrelationsdaten verfügbar."}
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className="placeholder-message">
            Bitte geben Sie einen gültigen EDA Pfad an und laden Sie die Daten,
            um das Dashboard anzuzeigen.
          </p>
        );

      case "model_info":
        // Use the ModelInfoCard component
        return canShowModel ? (
          <ModelInfoCard
            modelInfo={modelData.model_metrics.model_info}
            currentModelPath={modelData.model_path}
          />
        ) : (
          <p className="placeholder-message">
            Bitte geben Sie einen gültigen Modell Pfad an und laden Sie die
            Daten, um Modellinformationen anzuzeigen.
          </p>
        );

      case "model_dashboard":
        // Use the Model chart/table components
        return canShowModel ? (
          <div className="model-dashboard-layout">
            {/* Changed order: Feature Importance first */}
            {modelData.model_metrics.feature_importance_model && (
              <FeatureImportanceChart modelData={modelData.model_metrics} />
            )}
            {/* Then Model Performance Chart */}
            {modelData.model_metrics.metrics_per_target && (
              <ModelPerformanceChart modelData={modelData.model_metrics} />
            )}
            {/* Finally the Metrics Table */}
            {modelData.model_metrics.metrics_per_target && (
              <ModelMetricsTable modelData={modelData.model_metrics} />
            )}
          </div>
        ) : (
          <p className="placeholder-message">
            Bitte geben Sie einen gültigen Modell Pfad an und laden Sie die
            Daten, um Metriken anzuzeigen.
          </p>
        );

      // --- Add Predict Case ---
      case "predict":
        // Only render if model data is available
        return canShowModel ? (
          <PredictionForm
            modelData={modelData}
            edaData={edaData}
            currentModelPath={modelData.model_path}
            // Pass down state and handler
            isPredicting={isPredicting}
            predictionError={predictionError}
            predictionResult={predictionResult}
            onPredict={handlePredict} // Pass the callback
          />
        ) : (
          <p className="placeholder-message">
            Bitte laden Sie zuerst Modelldaten, um Vorhersagen zu treffen.
          </p>
        );

      // Add case for the new dashboard (placeholder for now)
      case "predict_dashboard":
        // Render the actual dashboard if result and data exist
        return predictionResult &&
          modelData &&
          modelData.model_metrics &&
          edaData ? (
          // Uncomment the actual component
          <PredictionResultDashboard
            predictionResult={predictionResult}
            modelData={modelData.model_metrics} // Übergibt DetailedModelMetrics
            edaData={edaData}
          />
        ) : (
          <p className="placeholder-message">
            Bitte führen Sie zuerst eine Vorhersage aus oder laden Sie die
            benötigten Model/EDA Daten.
          </p>
        );

      case "sensitivity_analysis":
        return canShowModel && modelData ? (
          <SensitivityAnalysisPage modelData={modelData} />
        ) : (
          <div className="chart-container card">
            <h2>Sensitivitätsanalyse</h2>
            <p>Bitte laden Sie Modelldaten, um die Analyse zu sehen.</p>
          </div>
        );
      case "pso_optimization":
        return canShowModel && modelData ? (
          <PSOOptimizationPage modelData={modelData} edaData={edaData} />
        ) : (
          <div className="chart-container card">
            <h2>PSO-Optimierung</h2>
            <p>
              Bitte laden Sie Modelldaten, um die PSO-Optimierung zu nutzen.
            </p>
          </div>
        );

      // Keep other sections as placeholders for now
      case "notifications":
        return (
          <div className="placeholder-message">Notifications Platzhalter</div>
        );
      case "users":
        return <div className="placeholder-message">People Platzhalter</div>;
      case "file_manager":
        return (
          <div className="placeholder-message">File Manager Platzhalter</div>
        );
      case "documentation":
        return (
          <div className="placeholder-message">Documentation Platzhalter</div>
        );

      default:
        // Show instructions if no data path is entered yet
        if (!currentEdaPath && !currentModelPath && !isLoading && !error) {
          return (
            <p className="placeholder-message instruction">
              Bitte geben Sie oben einen Pfad zu EDA Ergebnissen (.json) oder
              einem Modell (.joblib) ein und klicken Sie auf "Daten Laden".
            </p>
          );
        }
        // Fallback if a section ID is somehow invalid or no data loaded for it
        return (
          <p className="placeholder-message">
            Wählen Sie eine verfügbare Sektion aus dem Menü oder laden Sie
            Daten.
          </p>
        );
    }
  };

  return (
    // Add theme class to the main container
    <div className={`app-container ${theme}-theme`}>
      {/* Background */}
      <div className="background-layer" />

      <div className="layout-container">
        {/* Sidebar (mostly unchanged) */}
        <aside className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}>
          {/* ... sidebar header ... */}
          <div className="sidebar-header">
            <img src={logo} alt="MDESIGN Logo" className="logo" />
            {!isSidebarCollapsed && <h1>Data Insights</h1>}
            <button
              className="collapse-btn"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            >
              {isSidebarCollapsed ? "→" : "←"}
            </button>
          </div>

          {/* --- Sidebar Navigation --- */}
          <nav className="sidebar-nav">
            {/* Iterate over new menuSections structure */}
            {Object.entries(menuSections).map(([sectionKey, items]) => (
              <div key={sectionKey} className="nav-section">
                {!isSidebarCollapsed && (
                  // Use the key (eda, model, others, documents) as title
                  <h2 className="section-title">
                    {sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1)}
                  </h2>
                )}
                {items.map((item) => {
                  // Determine if the button should be disabled
                  const isDisabled =
                    (item.id.startsWith("eda_") && !edaData) ||
                    (item.id.startsWith("model_") && !modelData) ||
                    (item.id === "predict" && !modelData) ||
                    (item.id === "predict_dashboard" &&
                      (!modelData || !predictionResult)) || // Disable dashboard if no model or no result
                    isLoading;

                  return (
                    <button
                      key={item.id}
                      className={`nav-item ${activeSection === item.id ? "active" : ""}`}
                      disabled={isDisabled}
                      onClick={() => setActiveSection(item.id)}
                      title={
                        isDisabled
                          ? isLoading
                            ? "Daten werden geladen..."
                            : item.id.startsWith("eda_") && !currentEdaPath
                              ? "Bitte zuerst EDA Daten laden"
                              : item.id.startsWith("model_") &&
                                  !currentModelPath
                                ? "Bitte zuerst Model Daten laden"
                                : item.id.startsWith("eda_") && !edaData
                                  ? "EDA Daten konnten nicht geladen werden"
                                  : item.id.startsWith("model_") && !modelData
                                    ? "Model Daten konnten nicht geladen werden"
                                    : item.name
                          : item.name
                      }
                    >
                      <span className="icon">{item.icon}</span>
                      {!isSidebarCollapsed && (
                        <>
                          <span className="name">{item.name}</span>
                          {/* Badge/Star logic remains if needed */}
                          {/* {item.badge && <span className="badge">{item.badge}</span>} */}
                          {/* {item.favorite && <span className="star">⭐</span>} */}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* ... sidebar footer ... */}
          <div className="sidebar-footer">
            {/* Theme Toggle Button */}
            <button onClick={toggleTheme} className="theme-toggle-btn">
              {theme === "light" ? "🌙 Dark Mode" : "☀️ Light Mode"}
            </button>
            {/* Existing User Profile */}
            <div className="user-profile">
              <div className="avatar">👤</div>
              {!isSidebarCollapsed && (
                <div className="user-info">
                  <div className="name">Konrad Riedel</div>
                  <div className="role">Developer</div>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="main-content">
          {/* Anpassung: Einklappbarer Container für das Path-Input-Formular */}
          <div className="path-input-container card">
            <div
              className="path-input-header"
              onClick={() => setIsPathInputVisible(!isPathInputVisible)}
            >
              <h2 className="section-title">Datenquelle</h2>
              <button className="toggle-visibility-btn">
                {isPathInputVisible ? "Einklappen" : "Anzeigen"}
              </button>
            </div>

            {isPathInputVisible && (
              <PathInputForm
                onFetchData={fetchData}
                isLoading={isLoading}
                initialEdaPath={currentEdaPath}
                initialModelPath={currentModelPath}
              />
            )}
          </div>

          {/* Render section content dynamically */}
          {renderSectionContent()}
        </main>
      </div>
    </div>
  );
}

export default App;
